// src/components/ClientNotes.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  MessageSquare, 
  Plus, 
  Edit2, 
  Trash2, 
  Clock, 
  User,
  Tag,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { useIsMobile } from '../hooks/useMediaQuery'; // 👈 NUEVO IMPORT

/**
 * 🎯 Componente ClientNotes - Sistema profesional de notas internas
 * 
 * Funcionalidades:
 * - ✏️ Criar, editar e excluir notas
 * - 👤 Rastreamento de quem criou/editou
 * - 🏷️ Hashtags automáticas (#importante, #urgente, #lembrete)
 * - ⌨️ Atalho Ctrl+Enter para enviar
 * - 📊 Contador de notas
 * - 🎨 Cores dinâmicas por categoria
 * - 🕒 Timestamps formatados
 * - 📱 Adaptado para dispositivos móviles
 */

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return null;
  }
};

const extractHashtags = (text) => {
  const hashtagRegex = /#(\w+)/g;
  const matches = text.match(hashtagRegex);
  return matches ? matches.map(tag => tag.toLowerCase()) : [];
};

const getTagColor = (tag) => {
  const colors = {
    '#importante': 'bg-red-100 text-red-700 border-red-200',
    '#urgente': 'bg-orange-100 text-orange-700 border-orange-200',
    '#lembrete': 'bg-blue-100 text-blue-700 border-blue-200',
    '#ideia': 'bg-purple-100 text-purple-700 border-purple-200',
    '#observacao': 'bg-green-100 text-green-700 border-green-200'
  };
  
  return colors[tag] || 'bg-slate-100 text-slate-700 border-slate-200';
};

export const ClientNotes = ({ clientId, onUpdate }) => {
  // ==========================================================
  // Hooks y Contextos
  // ==========================================================
  const { database, openConfirm, user } = useAuth();
  const isMobile = useIsMobile(); // 👈 DETECTAR MÓVIL
  const notificationsCtx = (() => {
    try {
      return useNotifications();
    } catch {
      return null;
    }
  })();

  const notify = notificationsCtx?.notifySystem || 
                 notificationsCtx?.notify || 
                 ((msg) => console.log('📝 Nota:', msg));

  // ==========================================================
  // Estados Locales
  // ==========================================================
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  // ==========================================================
  // Cargar Notas
  // ==========================================================
  const loadNotes = useCallback(async () => {
    if (!database || !clientId) return;

    try {
      setLoading(true);
      const client = await database.getClientById(clientId);
      
      if (client && Array.isArray(client.notes)) {
        const sortedNotes = [...client.notes].sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        
        setNotes(sortedNotes);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar notas:', error);
      notify({
        type: 'danger',
        title: 'Erro ao carregar notas',
        message: 'Não foi possível carregar as notas do cliente.'
      });
    } finally {
      setLoading(false);
    }
  }, [database, clientId, notify]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes, clientId]);

  // ==========================================================
  // Contador de caracteres
  // ==========================================================
  useEffect(() => {
    setCharCount(newNote.length);
  }, [newNote]);

  // ==========================================================
  // Handlers - Adicionar Nota
  // ==========================================================
  const handleAddNote = useCallback(async () => {
    const trimmedNote = newNote.trim();
    
    if (!trimmedNote) {
      notify({
        type: 'warning',
        title: 'Nota vazia',
        message: 'Digite algum conteúdo para adicionar a nota.'
      });
      return;
    }

    if (trimmedNote.length > MAX_CHARS) {
      notify({
        type: 'warning',
        title: 'Nota muito longa',
        message: `Máximo de ${MAX_CHARS} caracteres permitidos.`
      });
      return;
    }

    if (!database || typeof database.addNoteToClient !== 'function') {
      notify({
        type: 'danger',
        title: 'Erro no sistema',
        message: 'Função de notas não disponível.'
      });
      return;
    }

    try {
      const result = await database.addNoteToClient(clientId, trimmedNote);
      
      if (result) {
        loadNotes();
        setNewNote('');
        notify({
          type: 'success',
          title: 'Nota adicionada',
          message: 'A nota foi salva com sucesso.'
        });
        onUpdate?.();
      }
    } catch (error) {
      console.error('❌ Erro ao adicionar nota:', error);
      notify({
        type: 'danger',
        title: 'Erro ao salvar',
        message: 'Não foi possível adicionar a nota. Tente novamente.'
      });
    }
  }, [database, clientId, newNote, notify, loadNotes, onUpdate]);

  // ==========================================================
  // Handlers - Editar Nota
  // ==========================================================
  const handleEditClick = useCallback((note) => {
    setEditingId(note.id);
    setEditText(note.text);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
  }, []);

  const handleSaveEdit = useCallback(async (noteId) => {
    const trimmedText = editText.trim();
    
    if (!trimmedText) {
      notify({
        type: 'warning',
        title: 'Nota vazia',
        message: 'A nota não pode ficar vazia.'
      });
      return;
    }

    if (trimmedText.length > MAX_CHARS) {
      notify({
        type: 'warning',
        title: 'Nota muito longa',
        message: `Máximo de ${MAX_CHARS} caracteres permitidos.`
      });
      return;
    }

    if (!database || typeof database.editNoteFromClient !== 'function') {
      notify({
        type: 'danger',
        title: 'Erro no sistema',
        message: 'Função de edição não disponível.'
      });
      return;
    }

    try {
      const result = database.editNoteFromClient(clientId, noteId, trimmedText);
      
      if (result) {
        loadNotes();
        setEditingId(null);
        setEditText('');
        notify({
          type: 'success',
          title: 'Nota atualizada',
          message: 'A nota foi editada com sucesso.'
        });
        onUpdate?.();
      }
    } catch (error) {
      console.error('❌ Erro ao editar nota:', error);
      notify({
        type: 'danger',
        title: 'Erro ao editar',
        message: 'Não foi possível editar a nota. Tente novamente.'
      });
    }
  }, [database, clientId, editText, notify, loadNotes, onUpdate]);

  // ==========================================================
  // Handlers - Excluir Nota (con confirmación)
  // ==========================================================
  const executeDeleteNote = useCallback((noteId) => {
    if (!database || typeof database.deleteNoteFromClient !== 'function') {
      notify({
        type: 'danger',
        title: 'Erro no sistema',
        message: 'Função de exclusão não disponível.'
      });
      return;
    }

    try {
      database.deleteNoteFromClient(clientId, noteId);
      loadNotes();
      notify({
        type: 'success',
        title: 'Nota excluída',
        message: 'A nota foi removida com sucesso.'
      });
      onUpdate?.();
    } catch (error) {
      console.error('❌ Erro ao excluir nota:', error);
      notify({
        type: 'danger',
        title: 'Erro ao excluir',
        message: 'Não foi possível excluir a nota. Tente novamente.'
      });
    }
  }, [database, clientId, notify, loadNotes, onUpdate]);

  const handleDeleteNote = useCallback((noteId) => {
    if (typeof openConfirm === 'function') {
      openConfirm({
        title: 'Excluir nota',
        message: 'Tem certeza que deseja excluir esta nota? Esta ação não pode ser desfeita.',
        type: 'warning',
        onConfirm: () => executeDeleteNote(noteId),
        onCancel: () => {
          notify({
            type: 'info',
            title: 'Operação cancelada',
            message: 'A nota não foi excluída.'
          });
        }
      });
    } else {
      if (window.confirm('Tem certeza que deseja excluir esta nota?')) {
        executeDeleteNote(noteId);
      }
    }
  }, [openConfirm, executeDeleteNote, notify]);

  // ==========================================================
  // Atajo de teclado (Ctrl+Enter)
  // ==========================================================
  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      handleAddNote();
    }
  }, [handleAddNote]);

  // ==========================================================
  // Extraer hashtags
  // ==========================================================
  const allHashtags = useMemo(() => {
    const tags = new Set();
    notes.forEach(note => {
      extractHashtags(note.text).forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, [notes]);

  // ==========================================================
  // Renderización - ADAPTADA
  // ==========================================================
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      
      {/* Header - ADAPTADO */}
      <div className={`border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white ${
        isMobile ? 'px-4 py-3' : 'px-6 py-4'
      }`}>
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center justify-between'}`}>
          <div className="flex items-center gap-2">
            <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-blue-100 rounded-xl`}>
              <MessageSquare className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <h2 className={`font-bold text-slate-900 ${isMobile ? 'text-sm' : 'text-lg'}`}>
                {isMobile ? 'Notas' : 'Notas e Observações'}
              </h2>
              <p className={`text-slate-500 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
                {notes.length} {notes.length === 1 ? 'nota' : 'notas'}
                {!isMobile && notes[0] && ` • Última: ${formatTimestamp(notes[0].createdAt)}`}
              </p>
            </div>
          </div>

          {/* Tags populares - scroll horizontal en móvil */}
          {allHashtags.length > 0 && (
            <div className={`flex ${isMobile ? 'overflow-x-auto pb-1 gap-1' : 'gap-1'}`}>
              {allHashtags.slice(0, isMobile ? 2 : 3).map(tag => (
                <span
                  key={tag}
                  className={`whitespace-nowrap rounded-full border ${getTagColor(tag)} ${
                    isMobile ? 'text-[8px] px-1.5 py-0.5' : 'text-xs px-2 py-1'
                  }`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Área de input - ADAPTADA */}
      <div className={`border-b border-slate-200 bg-slate-50/50 ${
        isMobile ? 'p-4' : 'p-6'
      }`}>
        <div className="space-y-2">
          <div className="relative">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isMobile ? "Use #importante, #urgente..." : "💡 Dica: Use #importante, #urgente, #lembrete para categorizar..."}
              className={`w-full border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all resize-none text-sm ${
                isMobile ? 'px-3 py-2 min-h-[80px]' : 'px-4 py-3 min-h-[100px]'
              }`}
              maxLength={MAX_CHARS}
            />
            <div className={`absolute right-2 bottom-2 flex items-center gap-1 ${
              isMobile ? 'text-[8px]' : 'text-xs'
            }`}>
              <span className={`${charCount > MAX_CHARS * 0.9 ? 'text-orange-500' : 'text-slate-400'}`}>
                {charCount}/{MAX_CHARS}
              </span>
              {charCount > MAX_CHARS * 0.9 && (
                <AlertCircle className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-orange-500`} />
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-1 text-slate-400 ${
              isMobile ? 'text-[8px]' : 'text-xs'
            }`}>
              <kbd className={`bg-slate-100 border border-slate-200 rounded-md text-slate-600 ${
                isMobile ? 'px-1 py-0.5' : 'px-2 py-1'
              }`}>
                Ctrl
              </kbd>
              <span>+</span>
              <kbd className={`bg-slate-100 border border-slate-200 rounded-md text-slate-600 ${
                isMobile ? 'px-1 py-0.5' : 'px-2 py-1'
              }`}>
                Enter
              </kbd>
            </div>

            <button
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              className={`bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl flex items-center gap-1 transition-all shadow-sm hover:shadow-md ${
                isMobile ? 'px-3 py-1.5 text-xs' : 'px-5 py-2'
              }`}
            >
              <Plus className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
              {isMobile ? 'Adicionar' : 'Adicionar Nota'}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de notas - ADAPTADA */}
      <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
        {loading ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="animate-pulse">
                <div className={`${isMobile ? 'h-16' : 'h-20'} bg-slate-100 rounded-xl`}></div>
              </div>
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className={`text-center ${isMobile ? 'py-8 px-3' : 'py-12 px-4'}`}>
            <div className={`mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3 ${
              isMobile ? 'w-12 h-12' : 'w-16 h-16'
            }`}>
              <MessageSquare className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-slate-400`} />
            </div>
            <h3 className={`font-medium text-slate-700 mb-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              Nenhuma nota ainda
            </h3>
            <p className={`text-slate-400 max-w-sm mx-auto ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
              {isMobile ? 'Adicione informações importantes.' : 'Adicione informações importantes sobre este cliente para ajudar no fechamento de negócios.'}
            </p>
          </div>
        ) : (
          <div className={`space-y-3 ${isMobile ? 'max-h-[350px]' : 'max-h-[500px]'} overflow-y-auto pr-2 custom-scrollbar`}>
            {notes.map((note) => (
              <div
                key={note.id}
                className="group relative border border-slate-200 rounded-xl hover:shadow-md transition-all bg-white"
              >
                {editingId === note.id ? (
                  // Modo edición - ADAPTADO
                  <div className={`space-y-2 ${isMobile ? 'p-3' : 'p-4'}`}>
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className={`w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-200 ${
                        isMobile ? 'p-2 text-xs' : 'p-2 text-sm'
                      }`}
                      autoFocus
                      maxLength={MAX_CHARS}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={handleCancelEdit}
                        className={`border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${
                          isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                        }`}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleSaveEdit(note.id)}
                        className={`bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors ${
                          isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                        }`}
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                ) : (
                  // Modo visualización - ADAPTADO
                  <div className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        {/* Hashtags */}
                        <div className={`flex flex-wrap gap-1 mb-1`}>
                          {extractHashtags(note.text).map(tag => (
                            <span
                              key={tag}
                              className={`inline-flex items-center gap-1 rounded-full border ${getTagColor(tag)} ${
                                isMobile ? 'text-[8px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5'
                              }`}
                            >
                              <Tag className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* Texto */}
                        <p className={`text-slate-800 whitespace-pre-wrap ${
                          isMobile ? 'text-xs' : 'text-sm'
                        }`}>
                          {note.text}
                        </p>
                      </div>

                      {/* Botones de acción - SIEMPRE VISIBLES EN MÓVIL */}
                      <div className={`flex gap-1 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        <button
                          onClick={() => handleEditClick(note)}
                          className={`hover:bg-slate-100 rounded-lg transition-colors ${
                            isMobile ? 'p-1' : 'p-2'
                          }`}
                          title="Editar nota"
                        >
                          <Edit2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className={`hover:bg-red-50 rounded-lg transition-colors ${
                            isMobile ? 'p-1' : 'p-2'
                          }`}
                          title="Excluir nota"
                        >
                          <Trash2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-red-500`} />
                        </button>
                      </div>
                    </div>

                    {/* Metadata - ADAPTADA */}
                    <div className={`flex items-center gap-2 mt-2 text-slate-400 border-t border-slate-100 pt-2 ${
                      isMobile ? 'text-[8px]' : 'text-xs'
                    }`}>
                      <span className="flex items-center gap-1">
                        <Clock className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />
                        {isMobile 
                          ? formatTimestamp(note.createdAt)?.substring(0, 10) 
                          : formatTimestamp(note.createdAt)}
                      </span>
                      
                      {!isMobile && (
                        <>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            ID: {note.createdBy?.slice(-4) || 'N/A'}
                          </span>

                          {note.editedAt && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="flex items-center gap-1 text-amber-500">
                                <Edit2 className="w-3 h-3" />
                                Editado
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS para scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default ClientNotes;